/*
 * This file is part of glowingblue/scheduled-posts.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat, Ian Morland.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Modal, { IInternalModalAttrs } from 'flarum/common/components/Modal';
import Switch from 'flarum/common/components/Switch';
import Button from 'flarum/common/components/Button';
import Stream from 'flarum/common/utils/Stream';
import { slug } from '../../common';
import type Mithril from 'mithril';

export interface ISchedulePostModalAttrs extends IInternalModalAttrs {
	isScheduled?: boolean;
	scheduledAt?: Date;
	onsubmit: (scheduledAt: Date, isScheduled: boolean) => void;
}

export default class SchedulePostModal<CustomAttrs extends ISchedulePostModalAttrs = ISchedulePostModalAttrs> extends Modal<CustomAttrs> {
	protected isScheduled!: Stream<boolean>;
	protected scheduledAt!: Stream<Date>;

	oninit(vnode: Mithril.Vnode<CustomAttrs, this>) {
		super.oninit(vnode);

		// By default the post is set as scheduled
		let isScheduled = true;
		// ... unless the attrs say differently
		if (typeof this.attrs.isScheduled === 'boolean') {
			isScheduled = this.attrs.isScheduled;
		}

		this.isScheduled = Stream(isScheduled);
		this.scheduledAt = Stream(this.attrs.scheduledAt || new Date());
	}

	title() {
		return app.translator.trans(`${slug}.forum.composer.schedule_post_title`);
	}

	className() {
		return 'SchedulePostModal Modal--small';
	}

	/**
	 * Format a Date object to datetime-local input format (YYYY-MM-DDTHH:MM)
	 */
	formatDateTimeLocal(date: Date): string {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		const hours = String(date.getHours()).padStart(2, '0');
		const minutes = String(date.getMinutes()).padStart(2, '0');
		return `${year}-${month}-${day}T${hours}:${minutes}`;
	}

	/**
	 * Get the minimum datetime (now) in datetime-local format
	 */
	getMinDateTime(): string {
		return this.formatDateTimeLocal(new Date());
	}

	content() {
		return (
			<div className="Modal-body">
				<div className="SchedulePostModal-form">
					<div className="Form-group">
						<Switch state={!!this.isScheduled()} onchange={this.isScheduled}>
							{app.translator.trans(`${slug}.forum.modal.schedule_switch_label`)}
						</Switch>
					</div>

					<div className={`Form-group${!this.isScheduled() ? ' disabled' : ''}`}>
						<label className="label">{app.translator.trans(`${slug}.forum.modal.schedule_at_label`)}</label>

						<input
							type="datetime-local"
							className="FormControl"
							disabled={!this.isScheduled()}
							value={this.formatDateTimeLocal(this.scheduledAt())}
							min={this.getMinDateTime()}
							onchange={(e: Event) => {
								const value = (e.target as HTMLInputElement).value;
								if (value) {
									this.scheduledAt(new Date(value));
								}
							}}
						/>
					</div>
					<div className="Form-group">
						<Button type="submit" className="Button Button--primary SchedulePostModal-SubmitButton" loading={this.loading}>
							{app.translator.trans(`${slug}.forum.modal.submit`)}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	onsubmit(e: SubmitEvent) {
		e.preventDefault();

		this.attrs.onsubmit(this.scheduledAt(), this.isScheduled());
		app.modal.close();
	}
}
